import { logResults } from '@lune/common/logger'
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Radio,
  Snackbar,
  TextField,
  Typography
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { DEFAULT_CONFIG, NOBODY_LIST, api as apiConfig } from '../api/config'
import { api as apiGroup } from '../api/group'
import { type ConfigGroupMask, type Group } from '../api/types'
import { Root } from '../paths'
import { MultipleSelectChip, noValues } from './multiselectchip'
import { buildTargets } from './targets'

export type ActionEffect = 'added' | 'removed' | 'moved' | 'none'

export interface ActionResult {
  effect: ActionEffect | 'errored'
  effectOn: string
  from?: string
  severity?: 'success' | 'error' | 'info'
  message: string
}

const MAX_GROUPS_PER_SET = 20
const MAX_ALIASES_PER_GROUP = 30

interface NewOrMoveAliasProps {
  action: 'Create' | 'Move'
  alias?: string
  targets?: string[]
  selectedCandidate?: Group
  title: string
  targetGroupLabel: string
  onAction: (
    newAlias: string,
    newRecipients: string[],
    selectedCandidate: Group,
    createNewGroup: boolean,
    description: string,
    expires: string
  ) => Promise<ActionResult>
}

interface GroupSource {
  group: Group
  createNewGroup: boolean
}

const Section: React.FC<{ text: string }> = ({ text }) => (
  <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
    {text}
  </Typography>
)

const Action: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Container
    sx={{
      mt: 3,
      p: 0,
      gap: 2,
      display: 'flex',
      justifyContent: 'flex-end'
    }}
  >
    {children}
  </Container>
)

const NewOrMoveAlias: React.FC<NewOrMoveAliasProps> = ({
  action,
  alias: aliasSource = '',
  targets: newRecipientsSource = noValues,
  selectedCandidate: selectedCandidateSource,
  title,
  targetGroupLabel,
  onAction
}) => {
  const onNavigate = useNavigate()
  const [loadedTick, setLoadedTick] = useState<string>('')
  const [targets, setTargets] = useState<string[]>([])
  const [newAlias, setNewAlias] = useState<string>(aliasSource)
  const [newRecipients, setNewRecipients] =
    useState<string[]>(newRecipientsSource)
  const [lastResult, setLastResult] = useState<ActionResult>({
    effect: 'none',
    effectOn: '',
    message: ''
  })
  const [groups, setGroups] = useState<Group[]>([])
  const [selectedCandidate, setSelectedCandidate] = useState<
    GroupSource | undefined
  >(undefined)
  const [groupMasks, setGroupMasks] = useState<ConfigGroupMask[]>([])
  const { clockTick } = useOutletContext<{ clockTick: string }>()
  const [executingAction, setExecutingAction] = useState<boolean>(false)
  const executingCandidateRef = useRef<GroupSource | undefined>(undefined)
  const [description, setDescription] = useState<string>('')
  const [expires, setExpires] = useState<string>('')

  const ready = loadedTick === clockTick

  useEffect(() => {
    void Promise.all([
      apiGroup.fetch
        .list()
        .then(logResults('groups/list'))
        .then((result) =>
          result.map(async (email) => await apiGroup.fetch.single(email))
        )
        .then(logResults('groups/get/all'))
        .then(async (result) => await Promise.all(result))
        .then((result) => {
          setGroups(result)
          setSelectedCandidate(undefined)
        }),
      buildTargets().then(setTargets),
      apiConfig.fetch
        .single(DEFAULT_CONFIG)
        .then(logResults('config/list'))
        .then((result) => result.groupMasks)
        .then(logResults('config/list/groupMasks'))
        .then(setGroupMasks)
    ]).then(() => setLoadedTick(clockTick))
  }, [clockTick])

  const alreadyExists = (alias: string): boolean =>
    alias.trim().length > 0 &&
    (groups.some(
      (g) =>
        g.aliases.some((a) => a.indexOf(`${alias}@`) === 0) ||
        g.email.indexOf(`${alias}@`) === 0
    ) ||
      targets.some((t) => t.indexOf(`${alias}@`) === 0))

  const existingGroups: GroupSource[] =
    groups !== undefined
      ? groups
          .filter(
            (g) =>
              isEqual(g.members, newRecipients, stringCompare) ||
              ((g.members ?? []).length === 0 &&
                isEqual(newRecipients, NOBODY_LIST, stringCompare))
          )
          .map((g) => ({ group: g, createNewGroup: false }))
      : []

  const newGroups: GroupSource[] = []

  if (newRecipients.length > 0) {
    // Find the proper mapping for names
    groupMasks
      .filter((g) => isEqual(g.targets, newRecipients))
      .forEach((match) => {
        let generatedGroupEmail: string | undefined
        let generatedGroupName: string | undefined

        // Find the next ID
        for (let i: number = 0; i < MAX_GROUPS_PER_SET; i++) {
          const candidateName = match.prefix.replace('<NNN>', i.toString())
          if (!alreadyExists(candidateName.split('@')[0])) {
            generatedGroupEmail = candidateName
            generatedGroupName = match.name.replace('<NNN>', i.toString())
            break
          }
        }

        if (
          generatedGroupName !== undefined &&
          generatedGroupEmail !== undefined
        ) {
          const newGroup: Group = {
            name: generatedGroupName,
            email: generatedGroupEmail,
            members: newRecipients,
            aliases: []
          }
          newGroups.push({ group: newGroup, createNewGroup: true })
        } else {
          const result: ActionResult = {
            effect: 'errored',
            effectOn: '',
            severity: 'error',
            message: 'Maximum number of groups reached'
          }
          setLastResult(result)
        }
      })
  }

  const candidates: GroupSource[] = [...existingGroups, ...newGroups]

  const isAliasFull: (group: Group) => boolean = (group) =>
    group.aliases.length === MAX_ALIASES_PER_GROUP

  const isMoving = action === 'Move'

  const isSourceAndTargetGroupsSame: (g: Group | undefined) => boolean = (g) =>
    isMoving && selectedCandidateSource?.email === g?.email

  const [prevCandidatesKey, setPrevCandidatesKey] = useState<string>('')
  const candidatesKey = candidates.map((c) => c.group.email).join(',')
  if (prevCandidatesKey !== candidatesKey) {
    setPrevCandidatesKey(candidatesKey)
    setSelectedCandidate(
      candidates.find(
        (g) => !isAliasFull(g.group) && !isSourceAndTargetGroupsSame(g.group)
      )
    )
  }

  const closeMessage = (): void => {
    const candidate = executingCandidateRef.current ?? selectedCandidate
    if (
      (lastResult.severity === 'success' || lastResult.severity === 'info') &&
      candidate !== undefined &&
      lastResult.effect !== 'none' &&
      lastResult.effectOn !== ''
    ) {
      const fromParam =
        lastResult.from !== undefined ? `&from=${lastResult.from}` : ''
      onNavigate(
        `${Root.root.url.toString()}?reload=${candidate.group.email}&${
          lastResult.effect
        }=${lastResult.effectOn}${fromParam}${window.location.hash}`
      )
      executingCandidateRef.current = undefined
    }
    setLastResult({ effect: 'none', effectOn: '', message: '' })
  }

  const cancel = (): void => {
    onNavigate(`${Root.root.url.toString()}${window.location.hash}`)
  }

  function executeAction(): void {
    if (selectedCandidate === undefined) {
      return
    }
    executingCandidateRef.current = selectedCandidate
    setExecutingAction(true)
    void onAction(
      newAlias,
      newRecipients,
      selectedCandidate.group,
      selectedCandidate.createNewGroup,
      description,
      expires
    ).then((result) => {
      setExecutingAction(false)
      if (result.severity === 'success' && action === 'Create') {
        if (navigator.clipboard !== undefined) {
          void navigator.clipboard.writeText(result.effectOn)
          result.message = `${result.message} (copied to clipboard)`
        }
      }
      setLastResult(result)
    })
  }

  const dataInput = (
    label: string,
    children: React.ReactNode
  ): React.JSX.Element => (
    <Container sx={{ mt: 2, mb: 2, p: 0 }}>{children}</Container>
  )

  const form = (
    <>
      {dataInput(
        'email',
        <TextField
          placeholder="new-email"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">@lunenetworks.com</InputAdornment>
            )
          }}
          value={newAlias}
          onChange={(e) => {
            setNewAlias(e.target.value)
          }}
          error={alreadyExists(newAlias)}
          helperText={
            !isMoving && alreadyExists(newAlias)
              ? 'Alias already exists'
              : undefined
          }
          disabled={isMoving}
        />
      )}
      {isMoving &&
        dataInput(
          'Recipients',
          <MultipleSelectChip
            values={newRecipientsSource}
            onSelectTarget={(targets) => {
              console.log('selected', targets)
            }}
          />
        )}
      {isMoving &&
        dataInput(
          'Created in',
          <List sx={{ border: '1px solid lightgray', borderRadius: 1 }}>
            <ListItemButton disabled>
              <ListItemIcon>
                <Radio checked />
              </ListItemIcon>
              <ListItemText
                primary={selectedCandidateSource?.name}
                secondary={selectedCandidateSource?.email}
              />
            </ListItemButton>
          </List>
        )}
      {isMoving && <Section text="Move Alias To" />}
      {dataInput(
        'Recipients',
        <MultipleSelectChip
          initialValues={newRecipientsSource}
          onSelectTarget={setNewRecipients}
        />
      )}
      {!isMoving &&
        dataInput(
          'Description',
          <TextField
            label="Description"
            placeholder="Optional note about this alias"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
            }}
            fullWidth
            multiline
            maxRows={3}
          />
        )}
      {!isMoving &&
        dataInput(
          'Expires',
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiration date (optional)"
              value={expires !== '' ? dayjs(expires) : null}
              onChange={(value: Dayjs | null) => {
                setExpires(value !== null ? value.format('YYYY-MM-DD') : '')
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
        )}
      {dataInput(
        targetGroupLabel,
        <>
          {groups === undefined && <LinearProgress />}
          {groups !== undefined &&
            candidates.length > 0 &&
            newRecipients.length > 0 && (
              <List
                sx={{
                  border: '1px solid darkgray',
                  borderRadius: 1
                }}
              >
                <ListSubheader sx={{ backgroundColor: 'transparent' }}>
                  Pick an Existing Group
                </ListSubheader>
                {candidates
                  .filter((g) => !g.createNewGroup)
                  .map((g) => (
                    <ListItemButton
                      key={g.group.email}
                      selected={
                        selectedCandidate !== undefined &&
                        g.group.email === selectedCandidate.group.email
                      }
                      onClick={() => {
                        setSelectedCandidate(g)
                      }}
                      disabled={
                        isAliasFull(g.group) ||
                        isSourceAndTargetGroupsSame(g.group)
                      }
                    >
                      <ListItemIcon>
                        {!isSourceAndTargetGroupsSame(g.group) && (
                          <Radio
                            checked={
                              selectedCandidate !== undefined &&
                              g.group.email === selectedCandidate.group.email
                            }
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          isSourceAndTargetGroupsSame(g.group)
                            ? `${g.group.name} (current location)`
                            : g.createNewGroup
                              ? `${g.group.name} (create new group)`
                              : g.group.name
                        }
                        secondary={`${g.group.email} (${g.group.aliases.length} aliases)`}
                      />
                    </ListItemButton>
                  ))}
                {newGroups.length > 0 && (
                  <>
                    <Divider />
                    <ListSubheader
                      sx={{
                        backgroundColor: 'transparent'
                      }}
                    >
                      Create a New Group
                    </ListSubheader>
                  </>
                )}
                {newGroups.map((g) => (
                  <ListItemButton
                    key={g.group.email}
                    selected={
                      selectedCandidate !== undefined &&
                      g.group.email === selectedCandidate.group.email
                    }
                    onClick={() => {
                      setSelectedCandidate(g)
                    }}
                    disabled={isAliasFull(g.group)}
                  >
                    <ListItemIcon>
                      <Radio
                        checked={
                          selectedCandidate !== undefined &&
                          g.group.email === selectedCandidate.group.email
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        g.createNewGroup
                          ? `${g.group.name} (create new group)`
                          : g.group.name
                      }
                      secondary={`${g.group.email} (${g.group.aliases.length} aliases)`}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          {groups !== undefined &&
            (newRecipients.length === 0 || candidates.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                Select the recipients above. If no matching group appears, ask
                to create a new profile for that combination of recipients.
              </Typography>
            )}
        </>
      )}
      <Action>
        <Button variant="outlined" onClick={cancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={executeAction}
          disabled={
            (!isMoving && alreadyExists(newAlias)) ||
            isSourceAndTargetGroupsSame(selectedCandidate?.group) ||
            selectedCandidate === undefined ||
            newAlias.trim().length === 0 ||
            newRecipients.length === 0 ||
            executingAction ||
            lastResult.severity === 'success' ||
            lastResult.severity === 'info'
          }
        >
          {action}
        </Button>
      </Action>
    </>
  )

  return (
    <Box sx={{ maxWidth: 680 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          p: 3
        }}
      >
        {form}
      </Paper>
      <Snackbar
        open={lastResult.message !== ''}
        autoHideDuration={1000}
        onClose={closeMessage}
      >
        <Alert variant="filled" severity={lastResult.severity}>
          {lastResult.message}
        </Alert>
      </Snackbar>
      <Backdrop open={!ready || executingAction}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

// Function that converts the arrays to strings
// and then compares the strings
function isEqual<T>(
  a: T[] | null | undefined,
  b: T[] | null | undefined,
  compareFn?: (a: T, b: T) => number
): boolean {
  return (
    (a ?? []).slice().sort(compareFn).join() ===
    (b ?? []).slice().sort(compareFn).join()
  )
}

function stringCompare(a: string, b: string): number {
  return a.localeCompare(b)
}

export default NewOrMoveAlias
